import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CalendarDays, UserCheck, Rocket, Search } from 'lucide-react';
import { intelAPI } from '../services/api';

const Intel = () => {
  const [tab, setTab] = useState('earnings');
  const [earnings, setEarnings] = useState([]);
  const [insider, setInsider] = useState([]);
  const [ipos, setIpos] = useState([]);
  const [insiderSym, setInsiderSym] = useState('AAPL');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    if (tab === 'earnings') {
      intelAPI.earnings(7).then((d) => setEarnings(d.items || [])).finally(() => setLoading(false));
    } else if (tab === 'ipos') {
      intelAPI.ipos(60).then((d) => setIpos(d.items || [])).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [tab]);

  const loadInsider = async () => {
    if (!insiderSym.trim()) return;
    setLoading(true);
    try {
      const d = await intelAPI.insider(insiderSym.toUpperCase(), 90);
      setInsider(d.items || []);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Market Intelligence</h1>
          <p className="text-gray-500">Earnings calendar, insider transactions, IPO calendar — Finnhub live.</p>
        </div>

        <div className="flex gap-2 mb-6">
          <Button variant={tab === 'earnings' ? 'default' : 'outline'} className={tab === 'earnings' ? 'bg-purple-600 hover:bg-purple-700' : ''} onClick={() => setTab('earnings')} data-testid="intel-tab-earnings"><CalendarDays size={14} className="mr-1" /> Earnings</Button>
          <Button variant={tab === 'insider' ? 'default' : 'outline'} className={tab === 'insider' ? 'bg-purple-600 hover:bg-purple-700' : ''} onClick={() => setTab('insider')} data-testid="intel-tab-insider"><UserCheck size={14} className="mr-1" /> Insider</Button>
          <Button variant={tab === 'ipos' ? 'default' : 'outline'} className={tab === 'ipos' ? 'bg-purple-600 hover:bg-purple-700' : ''} onClick={() => setTab('ipos')} data-testid="intel-tab-ipos"><Rocket size={14} className="mr-1" /> IPOs</Button>
        </div>

        {tab === 'earnings' && (
          <Card data-testid="intel-earnings-card">
            <CardHeader><CardTitle className="text-lg">Earnings — Next 7 Days</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-gray-500 text-sm py-4">Loading…</p> :
                !earnings.length ? <p className="text-gray-500 text-sm py-4">No upcoming earnings.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-600 border-b"><tr><th>Symbol</th><th>Date</th><th>Time</th><th>EPS Est</th><th>Rev Est</th></tr></thead>
                    <tbody>
                      {earnings.slice(0, 80).map((e, i) => (
                        <tr key={`${e.symbol}-${e.date}-${i}`} className="border-b hover:bg-gray-50">
                          <td className="py-1.5 font-semibold">{e.symbol}</td>
                          <td>{e.date}</td>
                          <td><Badge variant="outline" className="text-xs">{e.hour === 'bmo' ? 'Before Open' : e.hour === 'amc' ? 'After Close' : e.hour || '—'}</Badge></td>
                          <td>{e.eps_estimate != null ? `$${e.eps_estimate}` : '—'}</td>
                          <td>{e.revenue_estimate ? `$${(e.revenue_estimate / 1e9).toFixed(2)}B` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'insider' && (
          <Card data-testid="intel-insider-card">
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-lg">Insider Transactions</CardTitle>
                <div className="flex gap-2">
                  <Input className="w-32" value={insiderSym} onChange={(e) => setInsiderSym(e.target.value.toUpperCase())} data-testid="insider-symbol" />
                  <Button className="bg-purple-600 hover:bg-purple-700" onClick={loadInsider} data-testid="insider-load-btn"><Search size={14} /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? <p className="text-gray-500 text-sm py-4">Loading…</p> :
                !insider.length ? <p className="text-gray-500 text-sm py-4">Search a symbol to view insider activity.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-600 border-b"><tr><th>Date</th><th>Insider</th><th>Action</th><th className="text-right">Shares</th><th className="text-right">Price</th></tr></thead>
                    <tbody>
                      {insider.map((row, i) => (
                        <tr key={`${row.transactionDate}-${row.name}-${i}`} className="border-b hover:bg-gray-50">
                          <td className="py-1.5">{row.transactionDate}</td>
                          <td>{row.name}</td>
                          <td><Badge variant="outline" className={`text-xs ${row.change > 0 ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'}`}>{row.transactionCode}</Badge></td>
                          <td className="text-right">{row.change?.toLocaleString()}</td>
                          <td className="text-right">{row.transactionPrice ? `$${row.transactionPrice}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'ipos' && (
          <Card data-testid="intel-ipos-card">
            <CardHeader><CardTitle className="text-lg">IPO Calendar</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-gray-500 text-sm py-4">Loading…</p> :
                !ipos.length ? <p className="text-gray-500 text-sm py-4">No upcoming IPOs.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-600 border-b"><tr><th>Symbol</th><th>Name</th><th>Date</th><th>Exchange</th><th className="text-right">Price Range</th></tr></thead>
                    <tbody>
                      {ipos.map((ipo, i) => (
                        <tr key={`${ipo.symbol || ipo.name}-${ipo.date}-${i}`} className="border-b hover:bg-gray-50">
                          <td className="py-1.5 font-semibold">{ipo.symbol || '—'}</td>
                          <td>{ipo.name}</td>
                          <td>{ipo.date}</td>
                          <td>{ipo.exchange}</td>
                          <td className="text-right">{ipo.price || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Intel;
